/**
 * SOLO SE DESCARGA EL IDIOMA QUE SE VA A LEER.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ Antes `main.js` llevaba DENTRO los dos locales: 64,6 KB de español +      ║
 * ║ 60,7 KB de inglés comprimidos, y `main.js` bloquea el pintado. O sea que  ║
 * ║ todo el mundo pagaba el idioma que no lee antes de ver una sola letra.    ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * 🔴 LA TRAMPA QUE HACE QUE ESTO NO SIRVA DE NADA, Y CÓMO SE ESQUIVA.
 * Convertir los DOS `import` en `import()` parece el arreglo obvio y es peor:
 * el navegador baja `main.js` → lo ejecuta → recién ahí descubre que necesita
 * un idioma → lo pide. Ese viaje de ida y vuelta extra (~150 ms en 4G lenta) lo
 * pagaría **todo el mundo**, y hoy los dos idiomas llegan sin viaje extra: el
 * resultado neto sería neutro o peor.
 *
 * Por eso el español NO se descarga: se queda importado de forma estática, o
 * sea que sigue llegando dentro de `main.js`, sin ningún viaje extra, y la app
 * puede pintar texto en cuanto `main.js` llega. El único que se baja aparte es
 * el inglés, y **solo si el usuario lo pidió dentro de la app**.
 *
 * NO SE MIRA `navigator.language`. La app es para República Dominicana: el
 * español no es "el idioma más probable", es el idioma por defecto. Preguntarle
 * al teléfono solo serviría para que a un visitante con el móvil en inglés le
 * costara un viaje extra la primera pantalla — y de paso dejaría el arranque
 * colgando de una descarga que puede fallar.
 *
 * 🔴 Y ES LO QUE HACE IMPOSIBLE UNA PANTALLA CON CLAVES CRUDAS.
 * El español está compilado dentro del bundle, así que existe antes de que haya
 * red. Si la descarga del inglés falla, tarda o la corta un túnel, la app se
 * pinta en español — nunca en `vehicles.noVehicles`. El resguardo no es una
 * rama de código que haya que acordarse de escribir: es la estructura, y por
 * eso no cuesta ni un byte del ahorro.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import es from './locales/es.json';
import { loadLocale } from './localeLoader';

/** Clave de la preferencia en el aparato. Es la que ya usaba la app. */
export const LANG_KEY = 'mi-mecanico-lang';

/** El idioma que viaja DENTRO del bundle. Es el resguardo y el idioma por defecto. */
export const BUNDLED_LANG = 'es';

/**
 * Cualquier cosa que no sea inglés cae a español: la app es para República
 * Dominicana y solo hay dos paquetes. `en-US`, `en-GB` y `EN` son inglés.
 */
export function normalizeLang(raw) {
  if (!raw) return BUNDLED_LANG;
  const base = String(raw).toLowerCase().split('-')[0].trim();
  return base === 'en' ? 'en' : BUNDLED_LANG;
}

/**
 * Español, salvo que el usuario haya pedido inglés EN ESTE APARATO.
 *
 * No se pregunta al servidor ni al perfil a propósito: el idioma hace falta
 * antes de saber quién es el usuario, y esperar la sesión sería devolver los
 * segundos de pantalla en blanco que este cambio viene a quitar. Tampoco se
 * pregunta a `navigator.language` (ver la cabecera del archivo).
 */
export function detectLanguage() {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved) return normalizeLang(saved);
  } catch { /* modo privado / almacenamiento bloqueado */ }

  return BUNDLED_LANG;
}

/**
 * Techo de espera del arranque. Si el paquete de inglés no llega a tiempo NO se
 * deja la pantalla en blanco: se pinta en español (que ya está en memoria) y el
 * cambio se aplica solo cuando llegue. Esperar indefinidamente sería cambiar
 * "un idioma de más" por "ninguna pantalla", que es peor que el problema.
 */
export const BOOT_TIMEOUT_MS = 2500;

i18n
  .use(initReactI18next)
  .init({
    // Solo el idioma que viaja en el bundle. El otro se añade al vuelo con
    // `addResourceBundle` en cuanto se descarga.
    resources: { es: { translation: es } },
    lng: BUNDLED_LANG,
    fallbackLng: BUNDLED_LANG,
    interpolation: { escapeValue: false },
  });

function applyBundle(lng, bundle) {
  if (!i18n.hasResourceBundle(lng, 'translation')) {
    i18n.addResourceBundle(lng, 'translation', bundle, true, true);
  }
}

/**
 * Cambia el idioma A PETICIÓN DEL USUARIO y guarda la preferencia.
 *
 * Es la única puerta que escribe en `localStorage`, y por eso estar en inglés
 * significa siempre "alguien lo pidió aquí", nunca "lo adivinamos".
 *
 * @throws si el paquete no se puede descargar. El que llama debe DECÍRSELO al
 *         usuario: la app se queda en el idioma actual, entera y usable.
 */
export async function setAppLanguage(raw) {
  const target = normalizeLang(raw);
  if (target === i18n.language && i18n.hasResourceBundle(target, 'translation')) {
    return target;
  }
  if (!i18n.hasResourceBundle(target, 'translation')) {
    applyBundle(target, await loadLocale(target));
  }
  await i18n.changeLanguage(target);
  try { localStorage.setItem(LANG_KEY, target); } catch { /* almacenamiento bloqueado */ }
  return target;
}

/** El idioma con el que arranca esta carga (sin red de por medio). */
export const initialLanguage = detectLanguage();

/**
 * Se resuelve cuando la app ya puede pintar texto en el idioma correcto.
 * NUNCA se rechaza: en el peor caso se resuelve con `'es'`, que es el idioma
 * que ya está en memoria.
 *
 * PARA CASI TODO EL MUNDO ESTO NO ESPERA NADA: sin preferencia guardada el
 * idioma es español, la promesa ya viene resuelta y `src/index.js` pinta en el
 * mismo microtask. Solo espera quien ELIGIÓ inglés en este aparato, y se
 * prefiere esa espera al parpadeo de pintar en español y saltar a inglés medio
 * segundo después, que es un fallo visible en cada carga.
 *
 * Y esa espera es casi siempre CERO en la práctica: haber elegido inglés aquí
 * implica haber estado aquí antes, y `src/service-worker.js` precachea los
 * chunks que emite webpack (`precacheAndRoute(self.__WB_MANIFEST)`), así que el
 * paquete de inglés sale del disco y no de la red. El techo de 2,5 s cubre el
 * caso raro (caché borrada, SW aún sin activar) sin dejar pantalla en blanco.
 */
export const i18nReady = (() => {
  if (initialLanguage === BUNDLED_LANG) return Promise.resolve(BUNDLED_LANG);

  const applied = loadLocale(initialLanguage)
    .then((bundle) => {
      applyBundle(initialLanguage, bundle);
      return i18n.changeLanguage(initialLanguage).then(() => initialLanguage);
    })
    .catch(() => BUNDLED_LANG);

  const techo = new Promise((resolve) => {
    setTimeout(() => resolve(BUNDLED_LANG), BOOT_TIMEOUT_MS);
  });

  return Promise.race([applied, techo]);
})();

export default i18n;
